import type { ScoringConfigResponse } from "@shared/labs/evaluation-settings-schemas";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { VersionsPanel } from "./versions-panel";

interface PromptSectionProps {
  section: string;
}

export function PromptSection({ section }: PromptSectionProps) {
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const queryKey = ["labs", "evaluationSettingsGet", section];

  const { data, isLoading } = useQuery<ScoringConfigResponse>({
    queryKey,
    queryFn: () => rpcClient.labs.evaluationSettingsGet({ section }),
  });

  useEffect(() => {
    if (data && !initialized) {
      setContent(String(data.data ?? ""));
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.labs.evaluationSettingsPut({ section, data: content });
    },
    onSuccess: () => {
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Prompt salvo" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.labs.evaluationSettingsDelete({ section });
    },
    onSuccess: () => {
      setInitialized(false);
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Prompt restaurado para o padrão" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao restaurar", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <Textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          setIsDirty(true);
        }}
        rows={30}
        className="font-mono text-sm"
      />
      {data?.updatedAt && (
        <p className="text-xs text-muted-foreground">
          Última alteração: {new Date(data.updatedAt).toLocaleString("pt-BR")}
        </p>
      )}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => resetMutation.mutate()}
          disabled={resetMutation.isPending}
        >
          {resetMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4 mr-2" />
          )}
          Restaurar padrão
        </Button>
        <div className="flex items-center gap-2">
          <VersionsPanel
            kind="scoring_config"
            identifier={section}
            label={`Histórico · ${section}`}
            onRollback={() => {
              setInitialized(false);
              queryClient.invalidateQueries({ queryKey });
            }}
          />
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
